import { z } from 'zod';

import { occurrenceSchema, refineMarketInput } from '@/shared/lib/datetime-local';

/** Mirrors the Admin API validation (AdminCouponRequests.cs, DB-CP-01 CHECKs); the backend re-checks every rule. */
export const MIN_PERCENT_OFF = 1;
export const MAX_PERCENT_OFF = 90;
export const MAX_VALIDITY_DAYS = 3650;

const percentOff = z
  .number({ error: 'Enter a percent.' })
  .min(MIN_PERCENT_OFF, `At least ${MIN_PERCENT_OFF}%.`)
  .max(MAX_PERCENT_OFF, `At most ${MAX_PERCENT_OFF}% (the payment gateway cannot charge 0).`)
  .refine((value) => Number.isInteger(value * 100), 'At most 2 decimals.');

/** null = no limit. */
const optionalAmount = (label: string, name: string) =>
  z
    .number({ error: `Enter ${label} or leave it empty.` })
    .positive(`${name} must be more than 0.`)
    .nullable();

const brandScope = {
  appliesToAllBrands: z.boolean(),
  brandIds: z.array(z.number().int().positive()),
};

function refineScope<T extends { appliesToAllBrands: boolean; brandIds: number[] }>(
  value: T,
  ctx: z.RefinementCtx,
) {
  if (!value.appliesToAllBrands && value.brandIds.length === 0)
    ctx.addIssue({
      code: 'custom',
      path: ['brandIds'],
      message: 'Choose at least one brand, or apply the coupon to every brand.',
    });
}

/** The saved window, which untouched fields send back unchanged. */
export interface CouponWindow {
  validFrom: string;
  validTo: string;
}

/** datetime-local values in the coupon's market zone; converted to UTC instants at submit. */
const marketDateTime = z.string().min(1, 'Choose a date and time.');

const couponObject = z.object({
  kind: z.enum(['Personal', 'Public']),
  code: z
    .string()
    .refine(
      (value) => value.trim() === '' || /^[A-Za-z0-9-]{4,32}$/.test(value.trim()),
      'Use 4 to 32 letters, digits or dashes.',
    ),
  customerId: z.number().int().positive().nullable(),
  percentOff,
  maxDiscountAmount: optionalAmount('a cap', 'The cap'),
  minOrderAmount: optionalAmount('a minimum', 'The minimum'),
  ...brandScope,
  validFrom: marketDateTime,
  validFromOccurrence: occurrenceSchema,
  validTo: marketDateTime,
  validToOccurrence: occurrenceSchema,
  description: z.string().max(200, 'At most 200 characters.'),
});

export type CouponFormValues = z.infer<typeof couponObject>;

/**
 * The coupon form, with its window entered in the coupon's market zone. The window is compared as
 * UTC instants, after a time that does not exist or happens twice in the zone has been resolved.
 */
export function couponSchema(zone: string, original?: CouponWindow) {
  return couponObject.superRefine((value, ctx) => {
    if (value.kind === 'Public' && value.code.trim() === '')
      ctx.addIssue({ code: 'custom', path: ['code'], message: 'A public coupon needs a code.' });
    if (value.kind === 'Personal' && value.customerId === null)
      ctx.addIssue({
        code: 'custom',
        path: ['customerId'],
        message: 'Choose the customer this coupon is for.',
      });
    refineScope(value, ctx);

    const from = refineMarketInput(
      ctx,
      {
        path: 'validFrom',
        occurrencePath: 'validFromOccurrence',
        input: value.validFrom,
        occurrence: value.validFromOccurrence,
        original: original?.validFrom,
      },
      zone,
    );
    const to = refineMarketInput(
      ctx,
      {
        path: 'validTo',
        occurrencePath: 'validToOccurrence',
        input: value.validTo,
        occurrence: value.validToOccurrence,
        original: original?.validTo,
      },
      zone,
    );
    if (from && to && to <= from)
      ctx.addIssue({
        code: 'custom',
        path: ['validTo'],
        message: 'The end must be after the start.',
      });
  });
}

export const pointsTierSchema = z
  .object({
    pointsCost: z
      .number({ error: 'Enter the points it costs.' })
      .int('Whole points only.')
      .min(1, 'At least 1 point.')
      .max(1000000, 'At most 1,000,000 points.'),
    percentOff,
    maxDiscountAmount: optionalAmount('a cap', 'The cap'),
    ...brandScope,
    validityDays: z
      .number({ error: 'Enter how many days the coupon stays valid.' })
      .int('Whole days only.')
      .min(1, 'At least 1 day.')
      .max(MAX_VALIDITY_DAYS, `At most ${MAX_VALIDITY_DAYS} days.`),
  })
  .superRefine(refineScope);

export type PointsTierFormValues = z.infer<typeof pointsTierSchema>;
